import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VirtualTourSection = () => {
  const [activeView, setActiveView] = useState('entrance');

  const tourViews = [
  {
    id: 'entrance',
    name: 'Main Entrance',
    image: "https://images.unsplash.com/photo-1621189079794-761afec1da38",
    imageAlt: 'Modern restaurant entrance with warm lighting featuring wooden doors, glass panels, and welcoming outdoor seating area with potted plants',
    description: 'Welcome to Taco Street - where authentic flavors meet modern design'
  },
  {
    id: 'dining',
    name: 'Main Dining Area',
    image: "https://images.unsplash.com/photo-1662472460736-e26f7a49e90a",
    imageAlt: 'Spacious restaurant dining area with wooden tables, comfortable seating, warm ambient lighting, and colorful Mexican-inspired wall decorations',
    description: 'Comfortable seating with vibrant Mexican street art and warm lighting'
  },
  {
    id: 'counter',
    name: 'Order Counter',
    image: "https://images.unsplash.com/photo-1691589337332-9104dd2e5d5a",
    imageAlt: 'Modern restaurant service counter with menu boards, clean preparation area, and friendly staff ready to take orders in bright well-lit space',
    description: 'Fresh preparation visible from our open kitchen concept'
  },
  {
    id: 'lounge',
    name: 'Lounge Area',
    image: "https://images.unsplash.com/photo-1632258584901-5458887dd0df",
    imageAlt: 'Cozy restaurant lounge area with comfortable cushioned seating, low tables, decorative plants, and warm mood lighting creating intimate atmosphere',
    description: 'Relaxed seating perfect for groups and casual hangouts'
  },
  {
    id: 'outdoor',
    name: 'Outdoor Seating',
    image: "https://images.unsplash.com/photo-1607008830059-1a5d2ae4cdcb",
    imageAlt: 'Outdoor restaurant patio with wooden furniture, string lights overhead, green plants, and open-air dining setup under evening sky',
    description: 'Al fresco dining under the Bengaluru sky'
  },
  {
    id: 'bar',
    name: 'Beverage Bar',
    image: "https://images.unsplash.com/photo-1719903551851-4f9f688a836e",
    imageAlt: 'Modern beverage bar counter with colorful drink dispensers, fresh fruit displays, menu boards, and clean service area with bar stools',
    description: 'Fresh beverages and signature drinks prepared to order'
  }];


  const activeViewData = tourViews?.find((view) => view?.id === activeView);

  return (
    <section className="py-16 bg-[var(--color-background)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-foreground)] mb-4">
            360° Virtual Tour
          </h2>
          <p className="text-lg text-[var(--color-muted-foreground)] max-w-2xl mx-auto">
            Explore every corner of our restaurant from the comfort of your screen
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-[var(--color-card)] animate-slide-up">
              <div className="aspect-video relative">
                <Image
                  src={activeViewData?.image}
                  alt={activeViewData?.imageAlt}
                  className="w-full h-full object-cover" />

                
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {activeViewData?.name}
                  </h3>
                  <p className="text-white/90 text-sm">
                    {activeViewData?.description}
                  </p>
                </div>

                <div className="absolute top-4 right-4 flex gap-2">
                  <button className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-lg">
                    <Icon name="Maximize2" size={20} color="var(--color-foreground)" />
                  </button>
                  <button className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-lg">
                    <Icon name="Share2" size={20} color="var(--color-foreground)" />
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="default"
                iconName="ChevronLeft"
                iconPosition="left"
                onClick={() => {
                  const currentIndex = tourViews?.findIndex((v) => v?.id === activeView);
                  const prevIndex = currentIndex > 0 ? currentIndex - 1 : tourViews?.length - 1;
                  setActiveView(tourViews?.[prevIndex]?.id);
                }}>

                Previous
              </Button>
              
              <span className="text-sm text-[var(--color-muted-foreground)]">
                {tourViews?.findIndex((v) => v?.id === activeView) + 1} / {tourViews?.length}
              </span>
              
              <Button
                variant="outline"
                size="default"
                iconName="ChevronRight"
                iconPosition="right"
                onClick={() => {
                  const currentIndex = tourViews?.findIndex((v) => v?.id === activeView);
                  const nextIndex = currentIndex < tourViews?.length - 1 ? currentIndex + 1 : 0;
                  setActiveView(tourViews?.[nextIndex]?.id);
                }}>

                Next
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-[var(--color-foreground)] mb-4">
              Explore Areas
            </h3>
            
            {tourViews?.map((view) =>
            <button
              key={view?.id}
              onClick={() => setActiveView(view?.id)}
              className={`w-full text-left p-4 rounded-xl transition-all duration-300 ${
              activeView === view?.id ?
              'bg-[var(--color-primary)] text-[var(--color-primary-foreground)] shadow-lg' :
              'bg-[var(--color-card)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)]'}`
              }>

                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 ${
                activeView === view?.id ? 'ring-2 ring-white' : ''}`
                }>
                    <Image
                    src={view?.image}
                    alt={view?.imageAlt}
                    className="w-full h-full object-cover" />

                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{view?.name}</p>
                    <p className={`text-xs truncate ${
                  activeView === view?.id ? 'text-[var(--color-primary-foreground)]/80' : 'text-[var(--color-muted-foreground)]'}`
                  }>
                      Click to view
                    </p>
                  </div>
                  
                  {activeView === view?.id &&
                <Icon name="Check" size={20} color="currentColor" />
                }
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default VirtualTourSection;